"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import TestStudio from "@/components/test-studio";
import { getApiErrorMessage } from "@/lib/api/errors";
import { captureProductEvent } from "@/lib/posthog/product-events";
import { buildPostHogRequestContext } from "@/lib/posthog/request-context";
import type { DashboardTestEndpoint } from "@/lib/test/constants";
import styles from "./account-first-request.module.css";

type KeyRecord = { id: number; name: string | null; key_prefix: string };
type Progress = {
  hasSuccessfulRequest: boolean;
  hasCreatedStandardKey: boolean;
  hasApiUsage: boolean;
  sampleCompletedAt: string | null;
  customerCompletedAt: string | null;
  firstSuccessfulRequestAt: string | null;
  progressAvailable: boolean;
  successHistory: "complete" | "retained_history";
};
type Completion = { source: "sample" | "customer"; endpoint: DashboardTestEndpoint; modelName: string; progressSaved: boolean };

const comparisonModes = [
  { title: "Face to face", left: "face", right: "face" },
  { title: "Face to vector", left: "face", right: "vector" },
  { title: "Vector to vector", left: "vector", right: "vector" },
] as const;

function ComparisonGlyph({ kind }: { kind: "face" | "vector" }) {
  return kind === "face" ? (
    <svg className={`${styles.capabilityGlyph} ${styles.faceGlyph}`} viewBox="0 0 48 56" fill="none" focusable="false">
      <rect x="2.5" y="2.5" width="43" height="51" rx="9" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="24" cy="20" r="7" fill="currentColor" />
      <path d="M10.5 43.5c1-7.5 6-11.5 13.5-11.5s12.5 4 13.5 11.5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  ) : (
    <svg className={`${styles.capabilityGlyph} ${styles.vectorGlyph}`} viewBox="0 0 48 56" fill="none" focusable="false">
      <rect x="2.5" y="2.5" width="43" height="51" rx="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M11 17h5m5 0h6m5 0h5M11 28h11m5 0h10M11 39h5m5 0h16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="18.5" cy="17" r="1.5" fill="currentColor" />
      <circle cx="24.5" cy="28" r="1.5" fill="currentColor" />
      <circle cx="18.5" cy="39" r="1.5" fill="currentColor" />
    </svg>
  );
}

export default function AccountFirstRequest({
  freeCallsRemaining,
  monthlyLimit,
  isPostpaid = false,
  onRequestCompleted,
}: {
  freeCallsRemaining?: number;
  monthlyLimit?: number;
  isPostpaid?: boolean;
  onRequestCompleted?: () => void;
}) {
  const [keys, setKeys] = useState<KeyRecord[] | null>(null);
  const [selectedKeyId, setSelectedKeyId] = useState<number | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [requestRunning, setRequestRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressError, setProgressError] = useState(false);
  const [canCreate, setCanCreate] = useState(true);
  const [secret, setSecret] = useState<string | null>(null);
  const [secretVisible, setSecretVisible] = useState(false);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);
  const [completion, setCompletion] = useState<Completion | null>(null);
  const [language, setLanguage] = useState<"curl" | "python">("curl");
  const [codeCopied, setCodeCopied] = useState(false);
  const mounted = useRef(false);
  const secretField = useRef<HTMLInputElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const launcher = useRef<HTMLButtonElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);
  const confetti = useRef<HTMLDivElement>(null);
  const celebrated = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setProgressError(false);
    const results = await Promise.allSettled([
      fetch("/api/keys", { cache: "no-store" }).then(async (response) => {
        const data = await response.json();
        if (!response.ok || !Array.isArray(data.keys)) throw new Error(data.error || "key_fetch_failed");
        return data;
      }),
      fetch("/api/account/activation", { cache: "no-store" }).then(async (response) => {
        const data = await response.json();
        if (!response.ok || typeof data.hasSuccessfulRequest !== "boolean") throw new Error("progress_unavailable");
        return data as Progress;
      }),
    ]);
    if (!mounted.current) return;
    if (results[0].status === "fulfilled") {
      const data = results[0].value;
      setKeys(data.keys);
      setCanCreate(data.limits?.canCreateStandardKey !== false);
      setSelectedKeyId((current) => data.keys.some((key: KeyRecord) => key.id === current) ? current : data.keys[0]?.id ?? null);
    } else {
      setKeys(null);
      setError("Couldn’t load your keys. Please retry.");
    }
    if (results[1].status === "fulfilled") {
      const data = results[1].value;
      setProgress(data);

    } else {
      setProgressError(true);
    }
    if (!initialized.current) {
      initialized.current = true;
      const hasResult = results[1].status === "fulfilled" && results[1].value.hasSuccessfulRequest;
      const hasKey = results[0].status === "fulfilled" && results[0].value.keys.length > 0;
      const hasPriorUse = results[1].status === "fulfilled" &&
        (results[1].value.hasCreatedStandardKey || results[1].value.hasApiUsage || hasResult);
      const needsWelcome = results[0].status === "fulfilled" && !hasKey &&
        results[1].status === "fulfilled" && results[1].value.hasCreatedStandardKey === false &&
        results[1].value.hasApiUsage === false && !hasResult;
      setStep(hasKey && hasResult ? 4 : hasKey || hasPriorUse ? 3 : 0);
      setIsOpen(needsWelcome);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    mounted.current = true;
    void load();
    captureProductEvent("activation_viewed", { surface: "account" });
    return () => { mounted.current = false; };
  }, [load]);

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (!isOpen) {
      if (element.open) {
        element.close();
        launcher.current?.focus();
      }
      return;
    }
    if (!element.open) element.showModal();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      heading.current?.focus();
      if (content.current) content.current.scrollTop = 0;
    }
  }, [isOpen, step]);

  useEffect(() => {
    if (!isOpen || step !== 0 || celebrated.current) return;
    const layer = confetti.current;
    const motion = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!layer || motion?.matches) return;
    let frame = 0;
    let timeout = 0;
    const stop = () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      layer.hidden = true;
    };
    const start = () => {
      frame = requestAnimationFrame(() => {
        if (motion?.matches) return;
        celebrated.current = true;
        layer.hidden = false;
        timeout = window.setTimeout(stop, 4200);
      });
    };
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    motion?.addEventListener("change", stop);
    return () => {
      window.removeEventListener("load", start);
      motion?.removeEventListener("change", stop);
      stop();
    };
  }, [isOpen, step]);

  const handleCreate = async () => {
    if (creating || loading || keys === null || keys.length > 0) return;
    setCreating(true);
    setError(null);
    try {
      const response = await fetch("/api/keys", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: "First application", ...buildPostHogRequestContext() }),
      });
      const data = await response.json();
      if (!response.ok || !data.record?.id || !data.key) {
        throw new Error(data.error || "key_create_failed");
      }
      setKeys([data.record]);
      setSelectedKeyId(data.record.id);
      setSecret(data.key);
      captureProductEvent("activation_key_created", { surface: "account", result: "success" });
    } catch (cause) {
      // A lost response can still have created a key. Reconcile before allowing another POST.
      await load();
      setError(`${getApiErrorMessage(cause instanceof Error ? cause.message : "key_create_failed") || "Couldn’t confirm key creation."} If the secret is missing, replace the key in API keys.`);
    } finally {
      setCreating(false);
    }
  };

  const activeKey = keys?.find((key) => key.id === selectedKeyId);
  const sampleDone = completion?.source === "sample" || Boolean(progress?.sampleCompletedAt);
  const ownDone = completion?.source === "customer" || Boolean(progress?.customerCompletedAt);
  const requestDone = sampleDone || ownDone || Boolean(progress?.hasSuccessfulRequest);
  const endpoint = completion?.endpoint ?? "/represent";
  const model = completion?.modelName && /^[\w.-]+$/.test(completion.modelName) ? completion.modelName : null;
  const fields = endpoint === "/verify"
    ? ['  -F "img1=@face-a.jpg"', '  -F "img2=@face-b.jpg"']
    : ['  -F "img=@face.jpg"'];
  if (endpoint === "/compare") fields.push('  -F "target_vector=<target-vector.json"');
  if (model) fields.push(`  -F "model_name=${model}"`);
  const curl = [`curl --fail-with-body https://api.deepface.dev${endpoint}`, '  -H "x-api-key: $DEEPFACE_API_KEY"', ...fields].join(" \\\n");
  const dataFields = [
    ...(model ? [`"model_name": "${model}"`] : []),
    ...(endpoint === "/compare" ? ['"target_vector": json.dumps(target_vector)'] : []),
  ];
  const python = `# Install once: pip install requests\nimport os\n${endpoint === "/compare" ? "import json\n" : ""}import requests\n\n${endpoint === "/compare" ? 'with open("target-vector.json") as target:\n    target_vector = json.load(target)\n\n' : ""}${endpoint === "/verify" ? 'with open("face-a.jpg", "rb") as first, open("face-b.jpg", "rb") as second:' : 'with open("face.jpg", "rb") as image:'}\n    response = requests.post(\n        "https://api.deepface.dev${endpoint}",\n        headers={"x-api-key": os.environ["DEEPFACE_API_KEY"]},\n        files=${endpoint === "/verify" ? '{"img1": first, "img2": second}' : '{"img": image}'},${dataFields.length ? `\n        data={${dataFields.join(", ")}},` : ""}\n        timeout=120,\n    )\nresponse.raise_for_status()\nprint(response.json())`;
  const code = language === "curl" ? curl : python;

  const titles = ["Welcome to deepface.dev", "What can you do?", "Use it in your app", "Create your API key", "Get your first result"];
  const busy = creating || requestRunning;
  const localPreview = process.env.NODE_ENV === "development" && process.env.NEXT_PUBLIC_ONBOARDING_PREVIEW === "1";
  const launchLabel = ownDone || (requestDone && !sampleDone) ? "Open API tester" : sampleDone ? "Try your image" : activeKey ? "Continue setup" : "Start guide";

  return (
    <section className={styles.launcher} aria-label="Getting started">
      <div>
        <h2>{requestDone ? "Keep exploring" : "Get started"}</h2>
        <p>{ownDone ? "Your image tested. Ready for your app." : sampleDone ? "Sample tested. Try your own image next." : requestDone ? "Try another request or use the API in your app." : "A quick tour. Your first API result."}</p>
      </div>
      <button ref={launcher} className={styles.primary} disabled={loading} onClick={() => setIsOpen(true)}>{loading ? "Loading…" : launchLabel}</button>

      <dialog ref={dialog} className={styles.dialog} data-welcome={step === 0} aria-labelledby="onboarding-heading" onCancel={(event) => {
        event.preventDefault();
        if (!busy) setIsOpen(false);
      }}>
        <div ref={confetti} className={styles.confetti} data-testid="welcome-confetti" aria-hidden="true" hidden>
          {Array.from({ length: 56 }, (_, index) => <i key={index} style={{
            "--confetti-x": `${(index * 37 + 11) % 100}%`,
            "--confetti-drift": `${(index * 29) % 160 - 80}px`,
            "--confetti-delay": `${(index * 73) % 650}ms`,
            "--confetti-duration": `${2700 + (index * 47) % 750}ms`,
            "--confetti-turn": `${360 + (index * 53) % 540}deg`,
          } as CSSProperties} />)}
        </div>
        <div className={styles.topbar}>
          {step > 0 ? <span className={styles.stepLabel}>Step {step + 1} of 5</span> : null}
          {localPreview ? <span className={styles.previewBadge}>TEST DATA</span> : null}
          <button className={styles.close} aria-label="Close guide" disabled={busy} onClick={() => setIsOpen(false)}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
          </button>
        </div>
        <div ref={content} className={styles.content}>
          <h2 ref={heading} tabIndex={-1} id="onboarding-heading" className={step === 0 ? styles.welcomeTitle : undefined}>
            {/* Replace the whole welcome container: translation can detach its text nodes. */}
            <span key={step === 0 ? "welcome" : "step"}>
              {step === 0 ? <>Welcome to <span className={styles.welcomeBrand}>deepface.dev<span className={styles.welcomeEmoji} data-testid="welcome-emoji" aria-hidden="true">🎉</span></span></> : titles[step]}
            </span>
          </h2>
          {step === 0 ? <p className={styles.intro}>Thanks for joining us. Let’s get your first result.</p> : null}
          {step === 1 ? <>
            <p className={styles.intro}>Pick two inputs to compare.</p>
            <ol className={styles.capabilities} aria-label="Ways to compare">
              {comparisonModes.map(({ title, left, right }) => <li key={title} className={styles.capabilityCard} data-testid="capability-card">
                <div className={styles.capabilityDiagram} data-testid="capability-diagram" aria-hidden="true">
                  <ComparisonGlyph kind={left} />
                  <span className={styles.capabilityBridge}><span>↔</span></span>
                  <ComparisonGlyph kind={right} />
                </div>
                {title === "Face to vector" ? <div className={styles.vectorHelpWrap}>
                  <details className={styles.vectorHelp}>
                    <summary aria-label="What is a vector?" aria-controls="vector-definition"><h3>Face to <span className={styles.vectorTerm}>vector <span className={styles.vectorQuestion} aria-hidden="true">?</span></span></h3></summary>
                  </details>
                  <p id="vector-definition" className={styles.vectorDefinition}>A vector is face data saved as numbers.</p>
                </div> : <h3>{title}</h3>}
              </li>)}
            </ol>
          </> : null}
          {step === 2 ? <>
            <div className={styles.guideSections}>
              <section className={styles.guideSection} aria-labelledby="onboarding-connection-heading">
                <h3 id="onboarding-connection-heading">How it connects</h3>
                <div className={styles.apiFlow} aria-label="Your app connects to deepface.dev using an API key"><span>Your app</span><span aria-hidden="true">→</span><span>API key</span><span aria-hidden="true">→</span><span>deepface.dev</span></div>
              </section>
              <section className={styles.guideSection} aria-labelledby="onboarding-start-heading">
                <h3 id="onboarding-start-heading">Start here</h3>
                <p>{localPreview ? "Preview keys work here only." : "Try it here first. Then use the same key in your code."}</p>
              </section>
            </div>
          </> : null}
          {step >= 3 && (progressError || progress?.progressAvailable === false) ? <p className={styles.notice}>Saved progress is unavailable. You can still test.</p> : null}
          {step >= 3 && error ? <div className={styles.notice} role="alert"><p>{error}</p><button className={styles.secondary} disabled={busy || loading} onClick={() => void load()}>Retry loading</button> <Link href="/account/keys">API keys</Link></div> : null}
          {step === 3 ? <>
            {!activeKey ? <>
              <div className={styles.guideSections}>
                <section className={styles.guideSection} aria-labelledby="onboarding-key-use-heading">
                  <h3 id="onboarding-key-use-heading">What this key is for</h3>
                  <p>{localPreview ? "One test key for this preview." : "One key for testing here and building your app."}</p>
                </section>
                <section className={styles.guideSection} aria-labelledby="onboarding-usage-heading">
                  <h3 id="onboarding-usage-heading">Usage</h3>
                  <p>{localPreview ? "Test calls do not use credits." : isPostpaid ? "Requests use your monthly billing agreement." : typeof freeCallsRemaining === "number" && typeof monthlyLimit === "number" ? `${freeCallsRemaining} of ${monthlyLimit} free calls left this month.` : "Requests use your account’s allowance."}</p>
                  {!canCreate ? <p className={styles.notice}>Key limit reached. <Link href="/account/keys">Manage keys</Link> to continue.</p> : null}
                </section>
              </div>
            </> : <>
              <section className={styles.guideSection} aria-labelledby="onboarding-ready-heading">
                <h3 id="onboarding-ready-heading">Your API key</h3>
                {keys && keys.length > 1 ? <label className={styles.keySelect}>API key<select disabled={busy} value={selectedKeyId ?? ""} onChange={(event) => setSelectedKeyId(Number(event.target.value))}>{keys.map((key) => <option value={key.id} key={key.id}>{key.name || "Untitled key"} · {key.key_prefix}…</option>)}</select></label> : <p className={styles.keyName}>{activeKey.name || "Untitled key"} <code>{activeKey.key_prefix}…</code></p>}
                {secret ? <div className="ph-mask ph-no-capture" data-sensitive="api-key" data-session-replay-block="true">
                <label className={styles.secretLabel} htmlFor="first-request-secret">Save your key. It is only shown once.</label>
                <input ref={secretField} id="first-request-secret" type={secretVisible ? "text" : "password"} autoComplete="off" readOnly value={secret} className={styles.secretInput} />
                <div className={styles.secretActions}>
                  <button className={styles.secondary} aria-pressed={secretVisible} onClick={() => setSecretVisible(!secretVisible)}>{secretVisible ? "Hide key" : "Show key"}</button>
                  <button className={styles.secondary} onClick={async () => {
                    try { await navigator.clipboard.writeText(secret); setCopyStatus("Copied. Save it somewhere safe."); }
                    catch { setSecretVisible(true); requestAnimationFrame(() => { secretField.current?.focus(); secretField.current?.select(); }); setCopyStatus("Key selected. Use your browser’s copy command."); }
                  }}>Copy key</button>
                </div>
                <p className={styles.subtle} role="status">{copyStatus || "Keep it private. Store it on your server."}</p>
                </div> : <p className={styles.subtle}>No need to paste your key here. <Link href="/account/keys">Manage keys</Link></p>}
              </section>
            </>}
          </> : null}
          {step === 4 && activeKey ? <>
            <TestStudio key={activeKey.id} mode="dashboard" guided compact initialInputSource={requestDone ? "own" : "sample"} onRunningChange={setRequestRunning} apiKeyId={activeKey.id} apiKeyLabel={activeKey.name || activeKey.key_prefix} onCompleted={(result: Completion) => {
              setCompletion(result);
              onRequestCompleted?.();
              setProgressError(!result.progressSaved);
              setCodeCopied(false);
              void fetch("/api/account/activation", { cache: "no-store" }).then(async (response) => {
                const data = await response.json();
                if (!response.ok || typeof data.hasSuccessfulRequest !== "boolean") throw new Error("progress_unavailable");
                if (mounted.current) { setProgress(data); setProgressError(!result.progressSaved); }
              }).catch(() => { if (mounted.current) setProgressError(true); });
            }} />
            {requestDone ? <details className={styles.integration}>
              <summary>Use in your app</summary>
              <p>Set <code>DEEPFACE_API_KEY</code> on your server. Replace the image paths below.</p>
              {endpoint === "/compare" ? <p>Save a face vector from the same model in <code>target-vector.json</code>.</p> : null}
              <div className={styles.codeActions}>
                <div role="group" aria-label="Code language">
                  <button aria-pressed={language === "curl"} onClick={() => { setLanguage("curl"); setCodeCopied(false); }}>cURL</button>
                  <button aria-pressed={language === "python"} onClick={() => { setLanguage("python"); setCodeCopied(false); }}>Python</button>
                </div>
                <button className={styles.secondary} onClick={async () => {
                  try { await navigator.clipboard.writeText(code); setCodeCopied(true); captureProductEvent("activation_integration_copied", { surface: "account", method: language }); }
                  catch { setError("Select and copy the code below."); }
                }}>{codeCopied ? "Copied" : "Copy code"}</button>
              </div>
              <pre tabIndex={0} aria-label={`${language} request example`}><code>{code}</code></pre>
              <p className={styles.subtle}>Uses default detector and distance settings.</p>
              <a href="https://docs.deepface.dev/quickstart" target="_blank" rel="noreferrer">API quickstart ↗</a>
            </details> : null}
          </> : step === 4 ? <section className={styles.guideSection} aria-labelledby="onboarding-later-heading">
            <h3 id="onboarding-later-heading">Ready when you are</h3>
            <p>You can create a key later. You’ll need one to run your first request.</p>
            <button className={styles.secondary} onClick={() => setStep(3)}>Create a key</button>
          </section> : null}
        </div>
        <div className={styles.footer}>
          {step > 0 ? <button className={styles.secondary} disabled={busy} onClick={() => setStep(step - 1)}>Back</button> : <span />}
          {step < 3 || (step === 3 && activeKey) ? <button className={styles.primary} disabled={busy || loading} onClick={() => setStep(step + 1)}>Next</button> : step === 3 ? <div className={styles.footerActions}><button className={styles.secondary} disabled={busy || loading} onClick={() => setStep(4)}>Not now</button><button className={styles.primary} disabled={busy || loading || keys === null || !canCreate} onClick={handleCreate}>{creating ? "Creating…" : "Create API key"}</button></div> : <button className={styles.primary} disabled={busy} onClick={() => setIsOpen(false)}>{requestDone || !activeKey ? "Done" : "Close"}</button>}
        </div>
      </dialog>
    </section>
  );
}
